package com.pincer.core.model.tree

import com.pincer.core.Logger
import com.pincer.core.parsePincerException
import com.pincer.core.InvalidData
import com.pincer.core.ProblemWithRequest
import kotlin.time.Clock
import kotlinx.serialization.Serializable
import com.pincer.core.functions.MergePatch
import com.pincer.core.model.Schema
import com.pincer.core.model.patches.PincerPatch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.SerializationException
import kotlin.js.JsName

private val pincerJson       = Json { encodeDefaults = true }
// Merge patches leave out nulls, so a missing nullable field reads as null
private val pincerDecoder    = Json { explicitNulls = false }
private val prettyPincerJson = Json { encodeDefaults = true; prettyPrint = true }

@Serializable
data class Pincer

(

    @JsName("pid")             var pid             : String,
    @JsName("createdAt")       var createdAt       : Long,
    @JsName("parent")          var parent          : String?,
    @JsName("sid")             var sid             : Long,
    @JsName("sidParent")       var sidParent       : Long?,
    @JsName("sidPrincipal")    var sidPrincipal    : String,
    @JsName("pincerType")      var pincerType      : PincerType = PincerType.STANDARD,
    @JsName("days")            var days            : Boolean = false,
    @JsName("daysStart")       var daysStart       : String = "08:00",
    @JsName("daysEnd")         var daysEnd         : String = "20:00",
    @JsName("name")            var name            : String? = null,
    @JsName("description")     var description     : String? = null,
    @JsName("zone")            var zone            : String = "US/Eastern",
    @JsName("locale")          var locale          : String = "en-US",
    @JsName("rules")           var rules           : String = "true",
    @JsName("openRole")        var openRole        : String? = null,
    @JsName("forkRole")        var forkRole        : String? = null,
    @JsName("candidatesStale") var candidatesStale : Boolean = false,
    @JsName("selected")        var selected        : Candidate? = null,
    @JsName("chats")           val chats           : MutableMap<String,Chat> = mutableMapOf<String,Chat>(),
    @JsName("questions")       val questions       : MutableMap<String,Question> = mutableMapOf<String,Question>(),
    @JsName("participants")    val participants    : MutableMap<String,Participant> = mutableMapOf<String,Participant>(),
    @JsName("candidates")      val candidates      : MutableMap<String,Candidate> = mutableMapOf<String,Candidate>(),
    @JsName("permissions")     val permissions     : MutableMap<String,Permission> = mutableMapOf<String,Permission>(),
    @JsName("participations")  val participations  : MutableMap<String,Participation> = mutableMapOf<String,Participation>(),
    @JsName("localisations")   val localisations   : MutableMap<String,String> = mutableMapOf<String,String>(),
    @JsName("schemaVersion")   var schemaVersion   : Int = 1, // see Schema.kt

)

{

    companion object {

        fun parse(json: String): Pincer {
            try {
                val element = pincerDecoder.parseToJsonElement(json) as? JsonObject ?: throw SerializationException("Not an object")
                return pincerDecoder.decodeFromJsonElement(serializer(), Schema.pincer.migrateDocument(element))
            }
            catch (jde: SerializationException) { throw InvalidData("There's a problem with the Pincer json") }
        }

    }

    fun json(pretty:Boolean = false): String {
        return (if (pretty) prettyPincerJson else pincerJson).encodeToString(serializer(), this)
    }

    fun copyViaJson(): Pincer {
        return parse(json())
    }

    fun jsonElement(): JsonElement = pincerJson.encodeToJsonElement(serializer(), this)

    /*
    Applies a merge patch in place. Callers hold on to Pincer instances (client packs, Roz), so rather than
    return a new Pincer we copy the patched result back into this one.
    */
    fun applyPatch(patch: PincerPatch) {
        if (patch.isEmpty()) return
        val patched = try { pincerDecoder.decodeFromJsonElement(serializer(), MergePatch.apply(jsonElement(), Schema.pincer.migratePatch(patch.json))) }
                      catch (se: SerializationException) { throw InvalidData("That patch doesn't fit the Pincer: ${se.message}") }
        copyFrom(patched)
    }

    /** The merge patch that turns [ancestor] into this. */
    fun diff(ancestor: Pincer): PincerPatch =
        PincerPatch(MergePatch.diff(ancestor.jsonElement(), jsonElement()) as? JsonObject ?: JsonObject(emptyMap()))

    // The one place that has to list every field. A missed field shows up in MergePatchTests.
    private fun copyFrom(other: Pincer) {
        pid             = other.pid
        createdAt       = other.createdAt
        parent          = other.parent
        sid             = other.sid
        sidParent       = other.sidParent
        sidPrincipal    = other.sidPrincipal
        pincerType      = other.pincerType
        days            = other.days
        daysStart       = other.daysStart
        daysEnd         = other.daysEnd
        name            = other.name
        description     = other.description
        zone            = other.zone
        locale          = other.locale
        rules           = other.rules
        openRole        = other.openRole
        forkRole        = other.forkRole
        candidatesStale = other.candidatesStale
        selected        = other.selected
        chats.clear();          chats.putAll(other.chats)
        questions.clear();      questions.putAll(other.questions)
        participants.clear();   participants.putAll(other.participants)
        candidates.clear();     candidates.putAll(other.candidates)
        permissions.clear();    permissions.putAll(other.permissions)
        participations.clear(); participations.putAll(other.participations)
        localisations.clear();  localisations.putAll(other.localisations)
        schemaVersion   = other.schemaVersion
    }

    @JsName("participant")
    fun participant(id: String?): Participant? = if (id == null) null else participants[id]

    fun soleParticipant(): Participant {
        if (participants.size != 1) throw Exception("Not exactly one participant")
        return participants.values.first()
    }

}

public enum class PincerType() { STANDARD, PERSONAL, PAIR, TEMPLATE }

/*
PincerType is mostly there to assist in UI presentation of things.

- Personal pincers are never included in any kind of listings
- We wouldn't present a personal pincer in the same way as others, e.g. no picking a time or listing participants
- Pair pincers do get listed, but separately to all the others (e.g. Pairs are used for "your chats")
- These also don't have time choices fields displayed (except maybe as defaults for use elsewhere)
- Besides offering a starting point, templates are also there to group together the children

Note, there's some interesting cross-over between PincerType.TEMPLATE and a non-empty forkRole.
But the two things aren't an exact match. All templates get used for forks, but often all the participants
will already be on there. I'm not sure at this stage whether it makes sense for fork roles on
non-template pincers. You can certainly fork off a non-template pincer, but it would only make sense if
you cleaned up some of the non-template stuff like any selected times.
*/
